import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

const INK_COLOR = '#111827';
const INK_WIDTH = 2.4;
// Same thresholds the API enforces: a tap or a tiny scribble is not a signature.
const MIN_INK_LENGTH = 40;
const MIN_INK_EXTENT = 20;

const round = (n) => Math.round(n * 10) / 10;

const measureInk = (strokes) => {
  let length = 0;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  strokes.forEach((stroke) => {
    stroke.forEach(([x, y], i) => {
      if (i > 0) length += Math.hypot(x - stroke[i - 1][0], y - stroke[i - 1][1]);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });
  });
  const extent = Number.isFinite(minX) ? Math.max(maxX - minX, maxY - minY) : 0;
  return { length, extent };
};

/**
 * Hand-drawn signature pad (mouse, trackpad, pen and touch via Pointer Events).
 * Nothing leaves this component until the parent calls getSignature() on final confirmation.
 * Ref API: clear(), hasInk(), getSignature() → { width, height, strokes: [[[x, y], ...], ...] }, toDataURL().
 */
const SignaturePad = forwardRef(({ onChange, className = '' }, ref) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const drawingRef = useRef(false);
  const sizeRef = useRef({ width: 0, height: 0 });
  const [isBlank, setIsBlank] = useState(true);

  const context = () => canvasRef.current?.getContext('2d');

  const redraw = useCallback(() => {
    const ctx = context();
    const { width, height } = sizeRef.current;
    if (!ctx || !width) return;
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = INK_COLOR;
    ctx.fillStyle = INK_COLOR;
    ctx.lineWidth = INK_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    strokesRef.current.forEach((stroke) => {
      if (stroke.length === 1) {
        ctx.beginPath();
        ctx.arc(stroke[0][0], stroke[0][1], INK_WIDTH / 2, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(stroke[0][0], stroke[0][1]);
      for (let i = 1; i < stroke.length; i += 1) ctx.lineTo(stroke[i][0], stroke[i][1]);
      ctx.stroke();
    });
  }, []);

  // Keep the backing store matched to the rendered size × devicePixelRatio (crisp on HiDPI, correct after resize).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return undefined;

    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      if (!rect.width || !rect.height) return; // hidden (e.g. confirm step) — keep the existing drawing
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { width: rect.width, height: rect.height };
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      context().setTransform(dpr, 0, 0, dpr, 0, 0);
      redraw();
    };

    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(wrapper);
    window.addEventListener('resize', resize);

    // iOS Safari can still scroll/zoom on touch despite touch-action; block it only while touching the pad.
    const block = (e) => e.preventDefault();
    canvas.addEventListener('touchstart', block, { passive: false });
    canvas.addEventListener('touchmove', block, { passive: false });

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('touchstart', block);
      canvas.removeEventListener('touchmove', block);
    };
  }, [redraw]);

  const pointFrom = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
    return [round(x), round(y)];
  };

  const notify = () => {
    const { length, extent } = measureInk(strokesRef.current);
    onChange?.(length >= MIN_INK_LENGTH && extent >= MIN_INK_EXTENT);
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    try {
      canvasRef.current.setPointerCapture?.(e.pointerId);
    } catch {
      // Capture is an enhancement (keeps the stroke when the pointer leaves the pad); drawing works without it.
    }
    drawingRef.current = true;
    strokesRef.current.push([pointFrom(e)]);
    setIsBlank(false);
    redraw();
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    const events = typeof e.nativeEvent.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [];
    (events.length ? events : [e]).forEach((ev) => {
      const point = pointFrom(ev);
      const last = stroke[stroke.length - 1];
      if (last[0] !== point[0] || last[1] !== point[1]) stroke.push(point);
    });
    const ctx = context();
    const n = stroke.length;
    if (ctx && n > 1) {
      ctx.strokeStyle = INK_COLOR;
      ctx.lineWidth = INK_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      const from = Math.max(0, n - 1 - Math.max(events.length, 1));
      ctx.moveTo(stroke[from][0], stroke[from][1]);
      for (let i = from + 1; i < n; i += 1) ctx.lineTo(stroke[i][0], stroke[i][1]);
      ctx.stroke();
    }
  };

  const handlePointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try {
      canvasRef.current.releasePointerCapture?.(e.pointerId);
    } catch {
      // Already released.
    }
    notify();
  };

  useImperativeHandle(ref, () => ({
    clear() {
      strokesRef.current = [];
      drawingRef.current = false;
      setIsBlank(true);
      redraw();
      onChange?.(false);
    },
    hasInk() {
      const { length, extent } = measureInk(strokesRef.current);
      return length >= MIN_INK_LENGTH && extent >= MIN_INK_EXTENT;
    },
    getSignature() {
      const { width, height } = sizeRef.current;
      return {
        width: round(width),
        height: round(height),
        strokes: strokesRef.current.map((stroke) => stroke.map(([x, y]) => [x, y])),
      };
    },
    toDataURL() {
      return canvasRef.current?.toDataURL('image/png') || '';
    },
  }));

  return (
    <div
      ref={wrapperRef}
      className={`relative w-full h-[200px] sm:h-[220px] rounded-xl border-2 border-gray-300 bg-white overflow-hidden select-none ${className}`}
    >
      {isBlank ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">
          Draw your signature here
        </span>
      ) : null}
      <span aria-hidden="true" className="pointer-events-none absolute left-6 right-6 bottom-10 border-b border-gray-300" />
      <span aria-hidden="true" className="pointer-events-none absolute left-6 bottom-11 text-base text-gray-400">×</span>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Signature drawing area"
        className="absolute inset-0 w-full h-full cursor-crosshair"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse' && e.buttons === 0) handlePointerUp(e);
        }}
      />
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
